import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ListChecks,
  Inbox,
  Wallet,
  Coins,
  Trophy,
  Share2,
  UserRound,
  MessageSquare,
  Menu,
  LogOut,
  ChevronDown,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import NotificationsBell from "@/components/NotificationsBell";
import OnboardingTour from "@/components/OnboardingTour";
import BottomNav from "@/components/BottomNav";
import PageTransition from "@/components/PageTransition";
import SignOutOverlay from "@/components/SignOutOverlay";
import SupportChat from "@/components/SupportChat";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ensureUser, getWallet, authLogout, clearSession } from "@/lib/afritask";
import { BUILD_VERSION } from "@/lib/build-info";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/tasks", label: "Tasks", icon: ListChecks },
  { to: "/app/applications", label: "My Applications", icon: Inbox },
  { to: "/app/wallet", label: "Wallet", icon: Wallet },
  { to: "/app/earnings", label: "Earnings", icon: Coins },
  { to: "/app/leaderboard", label: "Leaderboard", icon: Trophy },
  { to: "/app/referrals", label: "Referrals", icon: Share2 },
  { to: "/app/profile", label: "Profile", icon: UserRound },
  { to: "/app/messages", label: "Message Centre", icon: MessageSquare },
];

const PAGE_TITLES = {
  "/app/dashboard": "Dashboard",
  "/app/tasks": "Available Tasks",
  "/app/applications": "My Applications",
  "/app/wallet": "Wallet",
  "/app/earnings": "Earnings",
  "/app/leaderboard": "Leaderboard",
  "/app/referrals": "Referrals",
  "/app/profile": "Profile",
  "/app/messages": "Message Centre",
  "/app/notifications": "Notifications",
};

function SidebarLinks({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-0.5 px-2 py-4">
      {NAV.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
              isActive
                ? "bg-amber-50 text-amber-800"
                : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
            }`
          }
        >
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const title = PAGE_TITLES[location.pathname] || "AfriTask AI";

  const refreshWallet = async () => {
    try {
      const w = await getWallet();
      setWallet(w);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const u = await ensureUser();
        if (!u) {
          navigate("/auth/enter-phone");
          return;
        }
        setMe(u);
        await refreshWallet();
        // Route new users to profile setup if not done yet
        if (u.onboarding_step === "profile" && location.pathname === "/app/dashboard") {
          navigate("/app/profile-setup");
        }
      } catch (e) {
        console.error(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh wallet on route change (e.g., after Paystack return)
  useEffect(() => {
    if (me) refreshWallet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const initials = me?.display_name
    ? me.display_name.replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase()
    : me?.username
    ? me.username.replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase()
    : "AT";

  const handleLogout = async () => {
    setLoggingOut(true);
    // Fire-and-forget the server-side logout while the overlay fades.
    // Even if it fails (network / 401), local state is cleared.
    authLogout().catch(() => {});
    // Match SignOutOverlay's fade timing (~800ms total)
    setTimeout(() => {
      clearSession();
      navigate("/");
    }, 800);
  };

  return (
    <div className="flex min-h-screen bg-neutral-50">
      {/* Global <AppToaster/> is mounted in App.js */}

      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-neutral-200 bg-white lg:flex lg:flex-col">
        <div className="flex h-16 items-center border-b border-neutral-200 px-5">
          <Logo />
        </div>
        <SidebarLinks />
        <div className="mt-auto border-t border-neutral-200 p-3">
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-neutral-600 transition hover:bg-neutral-100 hover:text-neutral-900"
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-neutral-200 bg-white px-4 sm:px-6">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <div className="flex h-full flex-col">
                <div className="flex h-16 items-center border-b border-neutral-200 px-5">
                  <Logo />
                </div>
                <SidebarLinks onNavigate={() => setMobileOpen(false)} />
                <div className="mt-auto border-t border-neutral-200 p-3">
                  <button
                    onClick={() => { setMobileOpen(false); handleLogout(); }}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-neutral-600 transition hover:bg-neutral-100 hover:text-neutral-900"
                    data-testid="mobile-sidebar-logout-btn"
                  >
                    <LogOut className="h-4 w-4" />
                    Log out
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <h1 className="text-base font-semibold tracking-tight text-neutral-900 sm:text-lg">
            {title}
          </h1>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => navigate("/app/wallet")}
              className="hidden items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 transition hover:bg-amber-100 sm:flex"
              data-testid="topbar-points-chip"
              id="topbar-wallet"
            >
              <span className="text-neutral-500">pts</span>
              <span className="tabular-nums">
                {wallet ? wallet.work_points.toLocaleString() : "—"}
              </span>
            </button>
            <button
              onClick={() => navigate("/app/earnings")}
              className="hidden items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 transition hover:bg-amber-100 sm:flex"
              data-testid="topbar-earnings-chip"
            >
              <span className="text-neutral-500">₦</span>
              <span className="tabular-nums">
                {wallet ? wallet.earnings_ngn.toLocaleString() : "—"}
              </span>
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="text-neutral-500 hidden"
              data-testid="topbar-bell-legacy"
            >
              <span />
            </Button>
            <NotificationsBell userReady={!!me} />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-lg border border-neutral-200 px-2 py-1 transition hover:bg-neutral-50"
                  data-testid="topbar-avatar"
                >
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-amber-700 text-xs font-semibold text-white">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden text-xs font-medium text-neutral-700 sm:inline">
                    {me?.display_name || me?.username || "…"}
                  </span>
                  <ChevronDown className="hidden h-3.5 w-3.5 text-neutral-400 sm:inline" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52" data-testid="user-menu">
                <DropdownMenuLabel className="truncate">
                  <p className="text-sm font-semibold text-neutral-900">{me?.display_name || me?.username || "…"}</p>
                  <p className="mt-0.5 truncate text-[11px] font-normal text-neutral-500">
                    {me?.phone || me?.user_id || ""}
                  </p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate("/app/profile")}
                  data-testid="user-menu-profile"
                >
                  <UserRound className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600 focus:bg-red-50 focus:text-red-700"
                  data-testid="user-menu-logout"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="min-w-0 flex-1 p-4 pb-24 sm:p-6 md:pb-8 lg:p-8">
          <PageTransition>
            <Outlet context={{ me, wallet, refreshWallet }} />
          </PageTransition>
        </main>
        {me && <OnboardingTour me={me} />}
      </div>
      {/* Mobile-only bottom nav — hidden during immersive task-workspace routes. */}
      {!/^\/app\/tasks\/[^/]+\/workspace/.test(location.pathname) && <BottomNav />}
      <SignOutOverlay show={loggingOut} />
      {me && <SupportChat me={me} />}
      {/* Build identifier — inspect [data-testid="build-version"] to compare
          preview vs production and confirm a deploy actually landed. */}
      <div
        data-testid="build-version"
        aria-hidden="true"
        className="pointer-events-none fixed bottom-1 right-1 z-[1] hidden font-mono text-[9px] text-neutral-300 md:block"
        title="Build identifier"
      >
        {BUILD_VERSION}
      </div>
    </div>
  );
}
